import type { ReactNode } from 'react';

import { Card, Skeleton } from '@/design-system';
import { ApiError, isApiError } from '@/shared/api';
import { ErrorState } from '@/shared/ui';

import { billingCopy } from '../copy';
import { describeBillingError } from '../errors';

export interface BillingRegionProps {
    title: string;
    query: { isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown };
    skeletonHeight?: number;
    children: ReactNode;
}

/**
 * Elke kaart wacht voor zichzelf: laden, een fout met "Opnieuw proberen", of de
 * inhoud. Eén trage of kapotte bron houdt de rest van het scherm niet tegen.
 */
export function BillingRegion({ title, query, skeletonHeight = 96, children }: BillingRegionProps) {
    if (query.isPending) {
        return (
            <Card padding="md" aria-busy="true">
                <Skeleton height={skeletonHeight} label={billingCopy.loading(title)} />
            </Card>
        );
    }

    if (query.isError) {
        return (
            <Card padding="md">
                <ErrorState
                    error={readableError(query.error)}
                    title={title}
                    onRetry={() => void query.refetch()}
                />
            </Card>
        );
    }

    return <>{children}</>;
}

/**
 * Een fout in woorden voor deze kaart. Offline blijft een offline-probleem, zodat
 * de melding rustig blijft (amber, geen rood); de rest krijgt een Nederlandse zin.
 */
function readableError(error: unknown): Error {
    if (isApiError(error) && error.kind === 'offline') {
        return new ApiError({
            kind: 'offline',
            status: 0,
            problem: error.problem,
            message: billingCopy.errorOfflineRead,
        });
    }

    return new Error(describeBillingError(error));
}
